import type { SetsResultDetail } from "./results";
import type { IrmCode, MeetOutcome } from "./vocab";

/**
 * Cricket (T20, sixes) — a SETS meet whose sides record INNINGS, not periods:
 * runs, wickets and balls per innings, in batting order across the match.
 *
 *   `innings[].seq`  the innings' place in the MATCH: 1 = batted first,
 *                    2 = the chase; 3 / 4, 5 / 6, 7 / 8 = super overs (the
 *                    odd seq bats first: the side that batted second).
 *                    So who batted first is data — the side holding seq 1 —
 *                    never a separate tick (the predecessor had no batting
 *                    order, and its "won by runs | wickets" was a radio button).
 *   `balls`          legal balls faced; shown as overs.balls ("19.4").
 *   `target`         a revised target (a reduced chase, DLS): the runs the chase needs.
 *   `overs`          the innings' own overs when reduced.
 *   `closed`         the operator ended the innings (declared, stopped).
 *   `tie_break_win`  a match level after its super overs, won by the rules' tie-break.
 *   `no_result`      the match was abandoned with no result (both sides).
 *
 * Who won is computed once the chase is OVER: more runs; level runs → the
 * super overs; none → a tie (DRAW). "Won by" is computed too: the side batting first
 * wins by its run margin, the chasing side by its wickets in hand (10 −
 * wickets lost) with the balls it had left. Source: MCC Laws of Cricket 16
 * (the result), ICC T20I Playing Conditions 16.10 (super over).
 */

export const BALLS_PER_OVER = 6;
export const CRICKET_WICKETS_MAX = 10;
/** The longest innings the config takes (ODI); T20 is 20, sixes 5 or 6. */
export const CRICKET_OVERS_MAX = 50;
/** Two innings and up to three super overs each (a level super over is played again — ICC T20I Playing Conditions). */
export const CRICKET_INNINGS_MAX = 8;
/** A super over ends at two wickets or six balls (ICC T20I Playing Conditions, super over appendix). */
export const SUPER_OVER_WICKETS = 2;
export const SUPER_OVER_BALLS = 6;
/** A typo guard on runs (the highest T20I total is 344). */
export const CRICKET_RUNS_MAX = 999;

export type CricketInnings = NonNullable<SetsResultDetail["innings"]>[number];

/** "19.4" from 118 balls; "20" from 120. */
export function oversText(balls: number): string {
	const o = Math.floor(balls / BALLS_PER_OVER);
	const b = balls % BALLS_PER_OVER;
	return b === 0 ? String(o) : `${o}.${b}`;
}

/** "19.4" → 118 balls; "20" → 120. null when it is not overs.balls with 0–5 balls. */
export function parseOvers(text: string): number | null {
	const m = /^\s*(\d{1,3})(?:\.([0-5]))?\s*$/.exec(text);
	if (!m) return null;
	return Number(m[1]) * BALLS_PER_OVER + (m[2] ? Number(m[2]) : 0);
}

/** The scorecard line of one innings: "159/7 (20 ov)", all out "142 (19.2 ov)". */
export function inningsText(inn: Pick<CricketInnings, "runs" | "wickets" | "balls">): string {
	const head = inn.wickets >= CRICKET_WICKETS_MAX ? `${inn.runs}` : `${inn.runs}/${inn.wickets}`;
	return `${head} (${oversText(inn.balls)} ov)`;
}

/** A side's innings in match order. */
const inningsOf = (d: SetsResultDetail | null | undefined): CricketInnings[] => [...(d?.innings ?? [])].sort((x, y) => x.seq - y.seq);

/** Both sides' innings in match order, each with its side. */
export function matchInnings(a: SetsResultDetail | null | undefined, b: SetsResultDetail | null | undefined): { side: 0 | 1; inn: CricketInnings }[] {
	return [...inningsOf(a).map((inn) => ({ side: 0 as const, inn })), ...inningsOf(b).map((inn) => ({ side: 1 as const, inn }))].sort(
		(x, y) => x.inn.seq - y.inn.seq || x.side - y.side,
	);
}

/** Does either side record innings — is this meet scored as cricket? */
export const hasInnings = (a: SetsResultDetail | null | undefined, b: SetsResultDetail | null | undefined): boolean =>
	(a?.innings?.length ?? 0) > 0 || (b?.innings?.length ?? 0) > 0;

export type CricketWonBy =
	| { unit: "runs"; n: number }
	| { unit: "wickets"; n: number; balls_left?: number }
	| { unit: "super_over" }
	| { unit: "tie_break" };

export interface CricketVerdict {
	/** Runs in the main innings (`result_value`). */
	score: [number, number];
	outcomes: readonly [MeetOutcome, MeetOutcome] | null;
	/** Who batted first: the side holding innings 1 (null while unknown). */
	battingFirst: 0 | 1 | null;
	/** How the winner won; null for a tie, a no-result, an IRM or while undecided. */
	wonBy: CricketWonBy | null;
	/** Abandoned with no result: no outcome, the no-result points. */
	noResult: boolean;
}

const WIN_LOSS: readonly [MeetOutcome, MeetOutcome] = ["WIN", "LOSS"];
const LOSS_WIN: readonly [MeetOutcome, MeetOutcome] = ["LOSS", "WIN"];

/**
 * Is a CHASE over — so the match can be decided? All out, its overs used
 * (`ballsMax`, when known), the runs it needs reached, or ended by the
 * operator (`closed`: declared, stopped). Until then a chase is being played:
 * 160/2 against 160 is not a tie, 80/2 is not a loss.
 */
export function chaseOver(inn: Pick<CricketInnings, "runs" | "wickets" | "balls" | "closed">, needed: number, ballsMax: number | undefined, wicketsMax: number = CRICKET_WICKETS_MAX): boolean {
	return inn.closed === true || inn.runs >= needed || inn.wickets >= wicketsMax || (ballsMax !== undefined && inn.balls >= ballsMax);
}

/**
 * Who won a cricket match, and how. `oversPerInnings` (config `overs`) gives
 * the chase's balls (an innings' own `overs` wins: a reduced chase).
 *
 *  · An IRM on exactly one side (a forfeit) → the other wins; on both → null.
 *  · `no_result` on either side → no outcome, `noResult`.
 *  · Both main innings needed (seq 1 and 2), and the chase OVER (`chaseOver`)
 *    — else null, whatever the score. The chase needs its `target` when one
 *    is stored (a revised, DLS-style target), else the first innings + 1.
 *    Reached → the chase wins by its wickets in hand (and the balls left);
 *    one short of it → level; fewer → the side batting first wins by
 *    (needed − 1 − chase runs).
 *  · Level → super overs, pair by pair (3 / 4, 5 / 6, 7 / 8; the even seq
 *    chases), each decided when its chase is over (2 wickets, 6 balls, the
 *    runs, or closed): the first pair that differs decides. Level with no
 *    super over → DRAW (a tie). Every super over level → a recorded
 *    `tie_break_win` on one side (boundary count…), else null (one more super over).
 */
export function cricketVerdict(
	sides: readonly [{ detail: SetsResultDetail | null | undefined; irm: IrmCode | null }, { detail: SetsResultDetail | null | undefined; irm: IrmCode | null }],
	oversPerInnings?: number,
): CricketVerdict {
	const [a, b] = sides;
	const all = matchInnings(a.detail, b.detail);
	const main = all.filter((x) => x.inn.seq <= 2);
	const runs = (side: 0 | 1, from: typeof all) => from.filter((x) => x.side === side).reduce((s, x) => s + x.inn.runs, 0);
	const score: [number, number] = [runs(0, main), runs(1, main)];
	const first = main.find((x) => x.inn.seq === 1)?.side ?? null;
	const base = { score, battingFirst: first, wonBy: null, noResult: false };
	const win = (side: 0 | 1, wonBy: CricketWonBy): CricketVerdict => ({ ...base, outcomes: side === 0 ? WIN_LOSS : LOSS_WIN, wonBy });
	if (a.irm !== null || b.irm !== null) {
		if (a.irm !== null && b.irm !== null) return { ...base, outcomes: null };
		return { ...base, outcomes: a.irm !== null ? LOSS_WIN : WIN_LOSS };
	}
	if (a.detail?.no_result === true || b.detail?.no_result === true) return { ...base, outcomes: null, noResult: true };
	const one = main.find((x) => x.inn.seq === 1);
	const two = main.find((x) => x.inn.seq === 2);
	if (!one || !two || one.side === two.side) return { ...base, outcomes: null };
	const needed = two.inn.target ?? one.inn.runs + 1;
	const overs = two.inn.overs ?? oversPerInnings;
	const ballsMax = overs !== undefined ? overs * BALLS_PER_OVER : undefined;
	if (!chaseOver(two.inn, needed, ballsMax)) return { ...base, outcomes: null };
	if (two.inn.runs >= needed) {
		return win(two.side, {
			unit: "wickets",
			n: Math.max(0, CRICKET_WICKETS_MAX - two.inn.wickets),
			...(ballsMax !== undefined ? { balls_left: Math.max(0, ballsMax - two.inn.balls) } : {}),
		});
	}
	if (two.inn.runs < needed - 1) return win(one.side, { unit: "runs", n: needed - 1 - two.inn.runs });

	// Level: the super overs, pair by pair.
	const superOvers = all.filter((x) => x.inn.seq > 2);
	if (superOvers.length === 0) return tieBreak(a.detail, b.detail, base, true);
	for (let k = 1; 2 * k + 1 <= CRICKET_INNINGS_MAX; k++) {
		const bat = superOvers.find((x) => x.inn.seq === 2 * k + 1);
		const chase = superOvers.find((x) => x.inn.seq === 2 * k + 2);
		if (!bat && !chase) break;
		if (!bat || !chase || bat.side === chase.side) return { ...base, outcomes: null };
		if (!chaseOver(chase.inn, bat.inn.runs + 1, SUPER_OVER_BALLS, SUPER_OVER_WICKETS)) return { ...base, outcomes: null };
		if (chase.inn.runs !== bat.inn.runs) return win(chase.inn.runs > bat.inn.runs ? chase.side : bat.side, { unit: "super_over" });
	}
	return tieBreak(a.detail, b.detail, base, false);
}

/** Level after everything played: a recorded tie-break win, else a DRAW (no super over) or undecided (one more super over). */
function tieBreak(
	a: SetsResultDetail | null | undefined,
	b: SetsResultDetail | null | undefined,
	base: Omit<CricketVerdict, "outcomes">,
	drawWhenNone: boolean,
): CricketVerdict {
	const ta = a?.tie_break_win === true;
	const tb = b?.tie_break_win === true;
	if (ta !== tb) return { ...base, outcomes: ta ? WIN_LOSS : LOSS_WIN, wonBy: { unit: "tie_break" } };
	return { ...base, outcomes: drawWhenNone ? ["DRAW", "DRAW"] : null };
}

/** "won by 24 runs", "won by 6 wickets (13 balls left)", "won the super over". */
export function wonByText(w: CricketWonBy): string {
	if (w.unit === "super_over") return "won the super over";
	if (w.unit === "tie_break") return "won on the tie-break";
	if (w.unit === "runs") return `won by ${w.n} ${w.n === 1 ? "run" : "runs"}`;
	const left = w.balls_left !== undefined && w.balls_left > 0 ? ` (${w.balls_left} ${w.balls_left === 1 ? "ball" : "balls"} left)` : "";
	return `won by ${w.n} ${w.n === 1 ? "wicket" : "wickets"}${left}`;
}
